import {
  CallState,
  ExperienceBookableItem,
  ExperienceId,
  ExperiencesPriceBreakdown,
  ExperiencesShopResponse,
  ExperiencesShopReviewsResponse,
  IExperiencesPassengerCounts,
  OpaqueToken,
  TrackingProperties,
  TravelWalletOffer,
} from "redmond";
import { actions, actionTypes } from "../actions";

export interface IExperiencesShopState {
  selectedExperienceId?: ExperienceId;
  experiencesShopCallState: CallState;
  experiencesShopDetails?: ExperiencesShopResponse;
  experiencesShopOptionsCallState: CallState;
  experiencesPriceBreakdowns?: ExperiencesPriceBreakdown[];
  experiencesShopReviewsCallState: CallState;
  selectedExperienceReviews?: ExperiencesShopReviewsResponse;
  travelerCounts?: IExperiencesPassengerCounts;
  selectedTime: string;
  selectedDate: string | null;
  selectedBookableItem: ExperienceBookableItem | null;
  shopOptionTrackingProperties?: TrackingProperties;
  activityIndex?: number;
  opaqueActivityToken?: OpaqueToken;
  nextAvailableDate?: string | null;
  nextAvailableDateCallState: CallState;
  // Captured in reducer before components can mutate timeSlotsByDate
  bookableItemDates?: string[];
  bestTravelOffer?: TravelWalletOffer;
}

export const initialState: IExperiencesShopState = {
  experiencesShopCallState: CallState.NotCalled,
  experiencesShopOptionsCallState: CallState.NotCalled,
  experiencesShopReviewsCallState: CallState.NotCalled,
  selectedTime: "",
  selectedDate: null,
  selectedBookableItem: null,
  nextAvailableDateCallState: CallState.NotCalled,
};

export const reducer = (
  state: IExperiencesShopState = initialState,
  action: actions.ExperiencesShopActions,
): IExperiencesShopState => {
  switch (action.type) {
    case actionTypes.FETCH_EXPERIENCES_SHOP:
      return {
        ...state,
        experiencesShopCallState: CallState.InProcess,
        experiencesShopDetails: undefined,
        nextAvailableDate: undefined,
        nextAvailableDateCallState: CallState.NotCalled,
      };
    case actionTypes.SET_EXPERIENCES_SHOP_DETAILS: {
      // Extract dates via direct index access; iteration methods fail
      // due to timeSlotsByDate being mutated by other components
      const bi = action.response.bookableItems ?? [];
      const dateSet = new Set<string>();
      for (let i = 0; i < bi.length; i++) {
        Object.keys(bi[i]?.timeSlotsByDate ?? {}).forEach((d) =>
          dateSet.add(d),
        );
      }
      return {
        ...state,
        experiencesShopCallState: CallState.Success,
        experiencesShopDetails: action.response,
        bookableItemDates: Array.from(dateSet).sort(),
        activityIndex: action.activtyIndex,
      };
    }
    case actionTypes.SET_SELECTED_EXPERIENCE:
      return {
        ...state,
        selectedExperienceId: action.experienceId,
      };
    case actionTypes.SET_EXPERIENCES_SHOP_CALL_FAILED:
      return {
        ...state,
        experiencesShopCallState: CallState.Failed,
      };
    case actionTypes.FETCH_EXPERIENCES_SHOP_OPTIONS:
      return {
        ...state,
        experiencesShopOptionsCallState: CallState.InProcess,
        experiencesPriceBreakdowns: undefined,
      };
    case actionTypes.SET_EXPERIENCES_SHOP_OPTIONS:
      return {
        ...state,
        experiencesShopOptionsCallState: CallState.Success,
        experiencesPriceBreakdowns: action.priceBreakdowns,
        shopOptionTrackingProperties: action.shopOptionTrackingProperties,
        opaqueActivityToken: action.opaqueActivityToken,
        // Update bestTravelOffer from shop_option response so the shop banner
        // reflects the offer for the currently selected date, not the initial shop date range
        bestTravelOffer: action.bestTravelOffer,
      };
    case actionTypes.SET_EXPERIENCES_SHOP_OPTIONS_CALL_FAILED:
      return {
        ...state,
        experiencesShopOptionsCallState: CallState.Failed,
        opaqueActivityToken: undefined,
      };
    case actionTypes.FETCH_EXPERIENCES_SHOP_REVIEWS:
      return {
        ...state,
        experiencesShopReviewsCallState: CallState.InProcess,
      };
    case actionTypes.SET_EXPERIENCES_SHOP_REVIEWS:
      return {
        ...state,
        experiencesShopReviewsCallState: CallState.Success,
        selectedExperienceReviews: action.response,
      };
    case actionTypes.SET_EXPERIENCES_SHOP_REVIEWS_CALL_STATE_FAILED:
      return {
        ...state,
        experiencesShopReviewsCallState: CallState.Failed,
      };
    case actionTypes.SET_TRAVELER_COUNTS:
      return {
        ...state,
        travelerCounts: action.counts,
      };
    case actionTypes.SET_SELECTED_TIME:
      return {
        ...state,
        selectedTime: action.selectedTime,
      };
    case actionTypes.SET_SELECTED_DATE:
      return {
        ...state,
        selectedDate: action.selectedDate,
      };
    case actionTypes.SET_SELECTED_BOOKABLE_ITEM:
      return {
        ...state,
        selectedBookableItem: action.selectedBookableItem,
      };
    case actionTypes.FIND_NEXT_AVAILABLE_DATE:
      return {
        ...state,
        nextAvailableDate: undefined,
        nextAvailableDateCallState: CallState.InProcess,
      };
    case actionTypes.SET_NEXT_AVAILABLE_DATE:
      return {
        ...state,
        nextAvailableDate: action.nextAvailableDate,
        nextAvailableDateCallState: CallState.Success,
      };
    default:
      return {
        ...state,
      };
  }
};

export * from "./selectors";
